import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n";

// EMDR bilateral animator. Value: { completed_rounds, speed_ms }.
// A round = one full left→right→left sweep. Validates at >= 10 rounds.
const SPEEDS = [{ v: 1300, key: "emdr_speed_slow" }, { v: 900, key: "emdr_speed_med" }, { v: 600, key: "emdr_speed_fast" }];

export function EMDRAnimator({ value, onChange }) {
  const t = useT();
  const [running, setRunning] = useState(!!value?.completed_rounds);
  const [rounds, setRounds] = useState(value?.completed_rounds || 0);
  const [speed, setSpeed] = useState(value?.speed_ms || 900);
  const [atRight, setAtRight] = useState(false);
  const [maxX, setMaxX] = useState(0);
  const trackRef = useRef(null);
  const halfRef = useRef(0);

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) setMaxX(Math.max(0, trackRef.current.clientWidth - 32 - 24));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setAtRight((p) => !p);
      halfRef.current += 1;
      if (halfRef.current % 2 === 0) {
        setRounds((r) => {
          const nr = r + 1;
          onChange({ ...(value || {}), completed_rounds: nr, speed_ms: speed });
          return nr;
        });
      }
    }, speed);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, speed]);

  const btnLabel = running ? t("widget_pause") : rounds === 0 ? t("widget_start") : t("widget_resume");

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t("emdr_hint")}</p>
      <div ref={trackRef} className="relative flex h-16 items-center rounded-full bg-secondary/60 px-3">
        <div
          className="h-8 w-8 rounded-full bg-primary"
          style={{ transform: `translateX(${atRight ? maxX : 0}px)`, transition: `transform ${speed}ms linear` }}
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="font-display text-base text-foreground" data-testid="emdr-rounds">{t("emdr_rounds")}: {rounds}</span>
        <button
          type="button"
          data-testid="emdr-toggle"
          onClick={() => setRunning((r) => !r)}
          className="rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors"
          style={{ backgroundColor: running ? "#C7893B" : "var(--primary, #5E7A73)" }}
        >
          {btnLabel}
        </button>
      </div>
      {rounds < 10 ? (
        <p className="text-xs text-muted-foreground" data-testid="emdr-min-rounds">{t("emdr_min_rounds", { n: rounds })}</p>
      ) : null}
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("emdr_speed")}</p>
      <div className="flex gap-2">
        {SPEEDS.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => setSpeed(o.v)}
            className={`rounded-full border px-3.5 py-2 text-sm transition-colors ${speed === o.v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary/60 text-foreground"}`}
          >
            {t(o.key)}
          </button>
        ))}
      </div>
    </div>
  );
}
